import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobStageQuestionsApi, plannedQuestionsApi } from '../services/recruitingApi';
import type { PlannedQuestion } from '../services/recruitingApi';

export const jobStageQuestionsKeys = {
  all: ['job-stage-questions'] as const,
  forJob: (jobId: string) => [...jobStageQuestionsKeys.all, 'job', jobId] as const,
  forStage: (jobId: string, stageId: string) => [...jobStageQuestionsKeys.all, jobId, stageId] as const,
};

export const plannedQuestionsKeys = {
  all: ['planned-questions'] as const,
  forInterview: (interviewId: string) => [...plannedQuestionsKeys.all, interviewId] as const,
};

// ===== Job-Stage Questions (Level 2) =====

export function useJobStageQuestions(jobId: string, stageId: string) {
  return useQuery({
    queryKey: jobStageQuestionsKeys.forStage(jobId, stageId),
    queryFn: () => jobStageQuestionsApi.getQuestions(jobId, stageId),
    enabled: !!jobId && !!stageId,
  });
}

export function useAllJobStageQuestions(jobId: string) {
  return useQuery({
    queryKey: jobStageQuestionsKeys.forJob(jobId),
    queryFn: () => jobStageQuestionsApi.getAllForJob(jobId),
    enabled: !!jobId,
  });
}

export function useUpdateJobStageQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, stageId, questions, scorecardTemplateId }: { jobId: string; stageId: string; questions: PlannedQuestion[]; scorecardTemplateId?: string | null }) =>
      jobStageQuestionsApi.updateQuestions(jobId, stageId, questions, scorecardTemplateId),
    onSuccess: (_data, { jobId, stageId }) => {
      queryClient.invalidateQueries({ queryKey: jobStageQuestionsKeys.forStage(jobId, stageId) });
      queryClient.invalidateQueries({ queryKey: jobStageQuestionsKeys.forJob(jobId) });
    },
  });
}

export function useCopyStageQuestionsFromTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => jobStageQuestionsApi.copyFromTemplate(jobId),
    onSuccess: (_data, jobId) => {
      queryClient.invalidateQueries({ queryKey: jobStageQuestionsKeys.forJob(jobId) });
    },
  });
}

export function useRefineJobStageQuestions() {
  return useMutation({
    mutationFn: ({ jobId, stageId, existingQuestions }: { jobId: string; stageId: string; existingQuestions: PlannedQuestion[] }) =>
      jobStageQuestionsApi.aiRefine(jobId, stageId, existingQuestions),
  });
}

// ===== Interview Planned Questions (Level 3) =====

export function usePlannedQuestions(interviewId: string) {
  return useQuery({
    queryKey: plannedQuestionsKeys.forInterview(interviewId),
    queryFn: () => plannedQuestionsApi.getQuestions(interviewId),
    enabled: !!interviewId,
  });
}

export function useUpdatePlannedQuestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ interviewId, questions }: { interviewId: string; questions: PlannedQuestion[] }) =>
      plannedQuestionsApi.updateQuestions(interviewId, questions),
    onSuccess: (_data, { interviewId }) => {
      queryClient.invalidateQueries({ queryKey: plannedQuestionsKeys.forInterview(interviewId) });
    },
  });
}

export function useRefineInterviewQuestions() {
  return useMutation({
    mutationFn: ({ interviewId, existingQuestions }: { interviewId: string; existingQuestions: PlannedQuestion[] }) =>
      plannedQuestionsApi.aiRefine(interviewId, existingQuestions),
  });
}
